import React, { useState, useRef } from "react";
import { api, formatErr } from "../lib/api";
import { toast } from "sonner";
import { Camera, X, Upload } from "lucide-react";

/**
 * AvatarUploader — subida de foto para cliente.
 * - Móvil/PC: usa <input file> con accept="image/*" (galería/archivos).
 * - Redimensiona en cliente a máx 512×512 y comprime a JPEG 0.85.
 * - Sube como data URL base64 al endpoint /admin/clients/{cid}/avatar.
 */
export default function AvatarUploader({ clientId, currentUrl, playerName, onUpdated, size = 96 }) {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentUrl || null);
  const inputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Selecciona una imagen válida");
    if (file.size > 8 * 1024 * 1024) return toast.error("La imagen es demasiado grande (máx 8 MB antes de comprimir)");
    setUploading(true);
    try {
      const dataUrl = await resizeImageToDataUrl(file, 512, 0.85);
      const { data } = await api.put(`/admin/clients/${clientId}/avatar`, { avatar_url: dataUrl });
      setPreview(data.avatar_url);
      onUpdated?.(data.avatar_url);
      toast.success("Foto actualizada");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setUploading(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  const remove = async () => {
    if (!window.confirm("¿Quitar la foto de perfil?")) return;
    try {
      await api.delete(`/admin/clients/${clientId}/avatar`);
      setPreview(null);
      onUpdated?.(null);
      toast.success("Foto eliminada");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const initial = (playerName || "?").charAt(0).toUpperCase();

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      <div style={{ position: "relative", width: size, height: size, borderRadius: "50%", overflow: "hidden", background: "#111", border: "2px solid rgba(255,255,255,0.14)", flexShrink: 0 }}>
        {preview ? (
          <img src={preview} alt={playerName || "avatar"} style={{ width: "100%", height: "100%", objectFit: "cover" }} data-testid="avatar-preview" />
        ) : (
          <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Bebas Neue, sans-serif", fontSize: size * 0.5, color: "#525252", letterSpacing: "0.04em" }}>{initial}</div>
        )}
        {uploading && (
          <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 11 }}>Subiendo…</div>
        )}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <input ref={inputRef} type="file" accept="image/*" style={{ display: "none" }}
               onChange={(e) => handleFile(e.target.files?.[0])} data-testid="avatar-file-input" />
        <button onClick={() => inputRef.current?.click()} disabled={uploading}
                style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#dc2626", color: "#fff", border: "none", padding: "7px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer", borderRadius: 2 }}
                data-testid="avatar-upload-btn">
          {preview ? <><Camera size={13}/> Cambiar</> : <><Upload size={13}/> Subir foto</>}
        </button>
        {preview && (
          <button onClick={remove} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "transparent", border: "1px solid rgba(255,255,255,0.14)", color: "#a3a3a3", padding: "5px 12px", fontSize: 11, cursor: "pointer", borderRadius: 2 }} data-testid="avatar-remove-btn">
            <X size={12}/> Quitar
          </button>
        )}
      </div>
    </div>
  );
}

export function AvatarCircle({ url, name, size = 32 }) {
  if (url) {
    return <img src={url} alt={name || "avatar"} style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover", background: "#111", border: "1px solid rgba(255,255,255,0.08)" }} />;
  }
  return (
    <div style={{ width: size, height: size, borderRadius: "50%", background: "#111", border: "1px solid rgba(255,255,255,0.08)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Bebas Neue, sans-serif", fontSize: size * 0.5, color: "#a3a3a3" }}>
      {(name || "?").charAt(0).toUpperCase()}
    </div>
  );
}

async function resizeImageToDataUrl(file, maxDim, quality) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      if (width > height && width > maxDim) { height = Math.round(height * (maxDim / width)); width = maxDim; }
      else if (height > maxDim) { width = Math.round(width * (maxDim / height)); height = maxDim; }
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    img.onerror = reject;
    const reader = new FileReader();
    reader.onload = (e) => { img.src = e.target.result; };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
